import PropTypes from "prop-types";
import { useContext, useMemo, useState, useEffect } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import dayjs from "dayjs";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import {
    Modal,
    Card,
    Grid,
    Icon,
    TextField,
    Autocomplete,
    Select,
    MenuItem,
    CircularProgress,
    InputLabel,
    Menu,
    Tooltip,
    Fade,
} from "@mui/material";

import { MessageManager } from "context";
import SoftInput from "components/SoftInput";
import SinDatos from "components/Carteles/SinDatos";
import { ConfirmarContext } from "context/ConfirmarContext";
import { getUproColor } from "utils/colors";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: { xs: "92%", sm: "70%", md: "55%", xxl: "45%" },
    overflowY: "auto",
    p: 3,
};

const GET_USUARIOS = gql`
  query getUsuariosCalendario($search: String) {
    usuarios(
      search: $search
      pagination: { page: 1, limit: 50 }
      order: { field: "apellido", order: ASC }
    ) {
      id
      nombre
      apellido
      dni
    }
  }
`;

const GET_TAGS_TAREA = gql`
    query tagsTarea($search: String) {
        tagsTarea(search: $search) {
            id
            nombre
        }
    }
`;

const CREAR_TAG_TAREA = gql`
    mutation crearTagTarea($nombre: String!) {
        crearTagTarea(nombre: $nombre) {
            id
            nombre
        }
    }
`;

const CREAR_TAREA = gql`
  mutation crearTarea($input: CrearTareaInput!) {
    crearTarea(input: $input) {
      id
      titulo
            observacion
      fecha
      estado
      periodicidad
      fechaLimite
    tipoTarea
    turno
            idUser
            idTag
            responsable { id nombre apellido dni }
            tag { id nombre }
            items { id texto checked }
    }
  }
`;

const EDITAR_TAREA = gql`
  mutation editarTarea($input: EditarTareaInput!) {
    editarTarea(input: $input) {
      id
      titulo
            observacion
      fecha
      estado
      periodicidad
      fechaLimite
    tipoTarea
    turno
            idUser
            idTag
            responsable { id nombre apellido dni }
            tag { id nombre }
            items { id texto checked }
    }
  }
`;

const CREAR_TAREA_PLANTILLA = gql`
    mutation crearTareaPlantilla($input: CrearTareaPlantillaInput!) {
        crearTareaPlantilla(input: $input) {
            id
            titulo
        }
    }
`;

const GET_TAREA_PLANTILLAS = gql`
    query tareaPlantillas($search: String) {
        tareaPlantillas(search: $search) {
            id
            titulo
            observacion
            tipoTarea
            turno
            idTag
            tag { id nombre }
            items { id texto checked }
        }
    }
`;

const ELIMINAR_TAREA_PLANTILLA = gql`
    mutation eliminarTareaPlantilla($id: ID!) {
        eliminarTareaPlantilla(id: $id) {
            id
        }
    }
`;


function formatUsuarioLabel(u) {
    if (!u) return "";
    const dni = u?.dni ? ` - DNI: ${u.dni} ` : "";
    return `${u.apellido || ""}, ${u.nombre || ""}${dni} `.trim();
}

export default function ModalTarea({ open, onClose, selectedDate, initialTask, onSaved }) {
    const { handleSnackbar } = useContext(MessageManager);

    const isEdit = Boolean(initialTask?.id);

    const [titulo, setTitulo] = useState("");
    const [observacion, setObservacion] = useState("");
    const [periodicidad, setPeriodicidad] = useState("UNICA");
    const [fechaLimite, setFechaLimite] = useState(null);
    const [searchUsuario, setSearchUsuario] = useState("");
    const [responsable, setResponsable] = useState(null);
    const [searchTag, setSearchTag] = useState("");
    const [tag, setTag] = useState(null);
    const [items, setItems] = useState([""]); // textos
    const [data, setData] = useState();

    const [anchorEl, setAnchorEl] = useState(null);
    const [openModalAddPlantiilla, setOpenModalAddPlantiilla] = useState(false);
    const [openMenuPlantilla, setOpenMenuPlantilla] = useState(false);
    const { confirmar } = useContext(ConfirmarContext);
    const [administrarPlantillas, setAdministrarPlantillas] = useState(false);

    const { data: usuariosData, loading: loadingUsuarios } = useQuery(GET_USUARIOS, {
        variables: { search: searchUsuario || null },
        fetchPolicy: "network-only",
        skip: !open,
    });

    const { data: tagsData, loading: loadingTags } = useQuery(GET_TAGS_TAREA, {
        variables: { search: searchTag || null },
        fetchPolicy: "network-only",
        skip: !open,
    });

    const usuariosOptions = useMemo(() => usuariosData?.usuarios || [], [usuariosData]);
    const tagsOptions = useMemo(() => tagsData?.tagsTarea || [], [tagsData]);

    const [crearTarea, { loading: loadingCreate }] = useMutation(CREAR_TAREA);
    const [editarTarea, { loading: loadingEdit }] = useMutation(EDITAR_TAREA);
    const [crearTagTarea, { loading: loadingCreateTag }] = useMutation(CREAR_TAG_TAREA);
    const [crearTareaPlantilla, { loading: loadingPlantilla }] = useMutation(CREAR_TAREA_PLANTILLA);
    const [eliminarTareaPlantilla, { loading: loadingDeletePlantilla }] = useMutation(ELIMINAR_TAREA_PLANTILLA, {
        refetchQueries: [{ query: GET_TAREA_PLANTILLAS }],
        awaitRefetchQueries: true,
    });

    const { data: plantillasData, loading: loadingPlantillas } = useQuery(GET_TAREA_PLANTILLAS, {
        fetchPolicy: "network-only",
        skip: !open,
    });

    useEffect(() => {
        if (!open) return;

        if (isEdit) {
            setTitulo(initialTask?.titulo || "");
            setObservacion(initialTask?.observacion || "");
            setPeriodicidad(initialTask?.periodicidad || "UNICA");
            setFechaLimite(initialTask?.fechaLimite ? dayjs(initialTask.fechaLimite) : null);
            setResponsable(initialTask?.responsable || null);
            setTag(initialTask?.tag || null);
            const currentItems = (initialTask?.items || []).map((it) => it?.texto).filter(Boolean);
            setItems(currentItems.length ? currentItems : [""]);
        } else {
            setTitulo("");
            setObservacion("");
            setPeriodicidad("UNICA");
            setFechaLimite(null);
            setResponsable(null);
            setTag(null);
            setItems([""]);
        }
    }, [open, isEdit, initialTask]);

    const canSubmit = useMemo(() => {
        if (!titulo.trim()) return false;
        if (!selectedDate || !selectedDate.isValid?.()) return false;
        const idUser = Number(responsable?.id);
        const idTag = Number(tag?.id);
        if (!Number.isFinite(idUser) || idUser <= 0) return false;
        if (!Number.isFinite(idTag) || idTag <= 0) return false;
        if (!items.map((t) => (t || "").trim()).filter(Boolean).length) return false;
        if ((periodicidad === "DIARIA" || periodicidad === "PERIODICA") && !fechaLimite) return false;
        if (!isEdit && selectedDate && selectedDate.isBefore(dayjs(), 'day')) return false;
        return true;
    }, [titulo, selectedDate, responsable, tag, items, periodicidad, fechaLimite]);

    const handleSubmit = async () => {
        if (!canSubmit) {
            handleSnackbar("Completa todos los campos requeridos", "error");
            return;
        }

        try {
            const idUser = Number(responsable?.id);
            const idTag = Number(tag?.id);

            if (isEdit) {
                await editarTarea({
                    variables: {
                        input: {
                            id: initialTask.id,
                            titulo: titulo.trim(),
                            observacion: observacion?.trim() || null,
                            idUser,
                            idTag,
                            items: items.map((t) => (t || "").trim()).filter(Boolean),
                        },
                    },
                });
                handleSnackbar("Tarea modificada", "success");
            } else {
                await crearTarea({
                    variables: {
                        input: {
                            titulo: titulo.trim(),
                            observacion: observacion?.trim() || null,
                            fecha: selectedDate.format("YYYY-MM-DD"),
                            periodicidad,
                            fechaLimite:
                                periodicidad === "DIARIA" || periodicidad === "PERIODICA"
                                    ? fechaLimite?.format("YYYY-MM-DD")
                                    : null,
                            tipoTarea: "CHECKLIST",
                            idUser,
                            idTag,
                            items: items.map((t) => (t || "").trim()).filter(Boolean),
                        },
                    },
                });
                handleSnackbar("Tarea creada", "success");
            }

            onSaved?.();
            onClose?.();
        } catch (e) {
            handleSnackbar(e?.message || "Error al guardar", "error");
        }
    };

    const handleSave = async () => {
        if (!data?.nombre) return;

        try {
            await crearTareaPlantilla({
                variables: {
                    input: {
                        titulo: data.nombre.trim(),
                        observacion: observacion?.trim() || null,
                        fecha: selectedDate.format("YYYY-MM-DD"),
                        periodicidad,
                        fechaLimite:
                            periodicidad === "DIARIA" || periodicidad === "PERIODICA"
                                ? fechaLimite?.format("YYYY-MM-DD")
                                : null,
                        tipoTarea: "CHECKLIST",
                        idTag: Number(tag.id),
                        items: items.map((t) => (t || "").trim()).filter(Boolean),
                    },
                },
            });
            handleSnackbar("Plantilla guardada con éxito", "success");
            setOpenModalAddPlantiilla(false);
            setData(null);
        } catch (e) {
            handleSnackbar(e?.message || "Error al guardar plantilla", "error");
        }
    };

    const loading = loadingCreate || loadingEdit || loadingCreateTag;

    const fechaLabel = selectedDate ? selectedDate.format("DD/MM/YYYY") : "-";

    const handleOpenPlantillaMenu = (event) => {
        setAnchorEl(event.currentTarget);
        setOpenMenuPlantilla(true);
    };

    const handleClosePlantillaMenu = () => {
        setAnchorEl(null);
        setOpenMenuPlantilla(false);
    };

    const plantillas = useMemo(() => {
        const all = plantillasData?.tareaPlantillas || [];
        return all.filter((p) => !p?.tipoTarea || p.tipoTarea === "CHECKLIST");
    }, [plantillasData]);

    const handleApplyPlantilla = (plantilla) => {
        if (plantilla.titulo) setTitulo(plantilla.titulo);
        if (plantilla.observacion) setObservacion(plantilla.observacion);
        if (plantilla.tag) setTag(plantilla.tag);

        const newItems = plantilla.items?.map((it) => it?.texto).filter(Boolean) || [];
        setItems(newItems.length ? newItems : [""]);

        handleClosePlantillaMenu();
        handleSnackbar("Plantilla aplicada", "success");
    };

    const handleDeletePlantilla = async (plantilla) => {
        if (!plantilla?.id) return;
        confirmar({
            title: "Eliminar plantilla",
            message: `¿Está seguro que desea eliminar la plantilla "${plantilla.titulo}"?`,
            func: async () => {
                try {
                    await eliminarTareaPlantilla({ variables: { id: plantilla.id } });
                    handleSnackbar("Plantilla eliminada", "success");
                } catch (e) {
                    handleSnackbar(e.message || "Error al eliminar plantilla", "error");
                }
            },
        });
    };

    const renderMenuPlantillas = (
        <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            open={Boolean(openMenuPlantilla)}
            onClose={handleClosePlantillaMenu}
        >
            {plantillas?.length > 0 ? (
                plantillas?.map((plantilla) => (
                    <Tooltip
                        key={plantilla.id}
                        title={`${administrarPlantillas ? "Eliminar" : ""} ${plantilla?.titulo} (${plantilla?.items?.length
                            } items)`}
                        placement="left"
                    >
                        <MenuItem
                            onClick={() => {
                                if (administrarPlantillas) handleDeletePlantilla(plantilla);
                                else if (items.some((it) => (it || "").trim() !== "")) {
                                    confirmar({
                                        title: "Sobreescribir items",
                                        message: "Esta acción sobreescribirá los items actuales, ¿desea continuar?",
                                        icon: "warning",
                                        func: () => {
                                            handleApplyPlantilla(plantilla);
                                        },
                                    });
                                } else {
                                    handleApplyPlantilla(plantilla);
                                }
                            }}
                            disabled={loadingPlantillas || loadingDeletePlantilla}
                        >
                            <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                    width: "100%",
                                }}
                            >
                                <SoftBox display="flex" mr={2}>
                                    <SoftBox mr={1}>
                                        <SoftTypography variant="p" fontWeight="bold">
                                            {plantilla.titulo.length > 20
                                                ? `${plantilla.titulo.substring(0, 20)}...`
                                                : plantilla.titulo}
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox>
                                        <SoftTypography
                                            variant="caption"
                                            color="uproGreen"
                                        >{`${plantilla?.items?.length} items`}</SoftTypography>
                                    </SoftBox>
                                </SoftBox>
                                <SoftBox>
                                    <Icon
                                        fontSize="small"
                                        sx={{
                                            verticalAlign: "middle",
                                            color: administrarPlantillas
                                                ? getUproColor("uproPink")
                                                : getUproColor("uproGreen"),
                                        }}
                                    >
                                        {administrarPlantillas ? "delete" : "playlist_add"}
                                    </Icon>
                                </SoftBox>
                            </SoftBox>
                        </MenuItem>
                    </Tooltip>
                ))
            ) : (
                <SinDatos />
            )}
            {plantillas?.length > 0 && (
                <MenuItem
                    onClick={() => setAdministrarPlantillas(!administrarPlantillas)}
                    disabled={loadingPlantillas || loadingDeletePlantilla}
                >
                    <SoftBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            width: "100%",
                        }}
                    >
                        <SoftBox display="flex" mr={2}>
                            <SoftBox mr={1}>
                                <SoftTypography variant="p" fontWeight="bold">
                                    {administrarPlantillas ? "Cancelar" : "Administrar"}
                                </SoftTypography>
                            </SoftBox>
                        </SoftBox>
                        <SoftBox>
                            <Icon
                                fontSize="small"
                                sx={{
                                    verticalAlign: "middle",
                                    color: getUproColor("uproYellow"),
                                }}
                            >
                                {administrarPlantillas ? "arrow_back" : "settings"}
                            </Icon>
                        </SoftBox>
                    </SoftBox>
                </MenuItem>
            )}
        </Menu>
    );



    return (
        <Modal open={open} onClose={onClose}>
            <Card sx={style}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <SoftTypography variant="h6" fontWeight="bold">
                        {isEdit ? "Editar tarea" : "Nueva tarea"}
                    </SoftTypography>
                    <SoftButton variant="text" color="dark" iconOnly onClick={onClose}>
                        <Icon>close</Icon>
                    </SoftButton>
                </SoftBox>

                {!isEdit && (
                    <SoftBox display="flex" justifyContent="end" alignItems="center" mr={1}>
                        <SoftBox mr={2}>
                            <Tooltip title="Plantillas" placement="top">
                                <SoftButton
                                    circular
                                    color="uproGreen"
                                    onClick={handleOpenPlantillaMenu}
                                    disabled={loadingPlantillas || loadingDeletePlantilla}
                                >
                                    {(loadingPlantillas || loadingDeletePlantilla) && (
                                        <CircularProgress size={15} color="inherit" />
                                    )}
                                    {!loadingPlantillas && !loadingDeletePlantilla && <Icon>sort</Icon>}
                                    &nbsp;Plantillas
                                </SoftButton>
                            </Tooltip>
                        </SoftBox>
                        {renderMenuPlantillas}
                        <SoftBox>
                            <Tooltip title="Guardar como plantilla" placement="top">
                                <SoftButton
                                    circular
                                    iconOnly
                                    color="primary"
                                    disabled={!canSubmit || loading}
                                    onClick={() => setOpenModalAddPlantiilla(true)}
                                >
                                    <Icon>save</Icon>
                                </SoftButton>
                            </Tooltip>
                        </SoftBox>
                    </SoftBox>
                )}

                <SoftBox mb={2}>
                    <SoftTypography variant="h6" color="secondary" fontWeight="bold">
                        Fecha: {fechaLabel}
                    </SoftTypography>
                </SoftBox>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <InputLabel htmlFor="titulo">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Título de la tarea
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                    &nbsp;*
                                </SoftTypography>
                            </SoftTypography>
                        </InputLabel>
                        <SoftInput
                            fullWidth
                            label="Título de la tarea"
                            value={titulo}
                            onChange={(e) => setTitulo(e.target.value)}
                            size="small"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <InputLabel htmlFor="observacion">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Observación
                            </SoftTypography>
                        </InputLabel>
                        <SoftInput
                            fullWidth
                            value={observacion}
                            onChange={(e) => setObservacion(e.target.value)}
                            size="small"
                            multiline
                            rows={3}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <InputLabel htmlFor="responsable">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Responsable
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                    &nbsp;*
                                </SoftTypography>
                            </SoftTypography>
                        </InputLabel>
                        <Autocomplete
                            options={usuariosOptions}
                            value={responsable}
                            loading={loadingUsuarios}
                            onChange={(e, newValue) => setResponsable(newValue)}
                            getOptionLabel={formatUsuarioLabel}
                            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                            onInputChange={(e, value) => setSearchUsuario(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingUsuarios ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                    </Grid>

                    <Grid item xs={12} md={6}>
                        <InputLabel htmlFor="tag">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Tag
                                <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                    &nbsp;*
                                </SoftTypography>
                            </SoftTypography>
                        </InputLabel>
                        <Autocomplete
                            options={tagsOptions}
                            value={tag}
                            loading={loadingTags}
                            onChange={async (e, newValue) => {
                                if (newValue && typeof newValue === "string") {
                                    try {
                                        const res = await crearTagTarea({ variables: { nombre: newValue } });
                                        setTag(res?.data?.crearTagTarea || null);
                                    } catch (err) {
                                        handleSnackbar(err?.message || "Error al crear tag", "error");
                                    }
                                    return;
                                }

                                if (newValue && newValue.inputValue) {
                                    try {
                                        const res = await crearTagTarea({ variables: { nombre: newValue.inputValue } });
                                        setTag(res?.data?.crearTagTarea || null);
                                    } catch (err) {
                                        handleSnackbar(err?.message || "Error al crear tag", "error");
                                    }
                                    return;
                                }

                                setTag(newValue);
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === "string") return option;
                                if (option?.inputValue) return option.inputValue;
                                return option?.nombre || "";
                            }}
                            isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                            onInputChange={(e, value) => setSearchTag(value)}
                            freeSolo
                            filterOptions={(options, params) => {
                                const filtered = options.filter((opt) =>
                                    (opt?.nombre || "").toLowerCase().includes((params.inputValue || "").toLowerCase())
                                );

                                const inputValue = (params.inputValue || "").trim();
                                const exists = options.some((opt) => (opt?.nombre || "").toLowerCase() === inputValue.toLowerCase());
                                if (inputValue !== "" && !exists) {
                                    filtered.push({ inputValue, nombre: `Crear: ${inputValue} ` });
                                }

                                return filtered;
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingTags ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <SoftTypography variant="caption" color="text" fontWeight="bold">
                            Items
                            <SoftTypography variant="caption" fontWeight="bold" color="primary">
                                &nbsp;*
                            </SoftTypography>
                        </SoftTypography>

                        <SoftBox mt={1} display="flex" flexDirection="column" gap={1}>
                            {items.map((value, idx) => (
                                <SoftBox key={idx} display="flex" alignItems="center" gap={1}>
                                    <SoftInput
                                        fullWidth
                                        size="small"
                                        value={value}
                                        onChange={(e) => {
                                            const next = [...items];
                                            next[idx] = e.target.value;
                                            setItems(next);
                                        }}
                                        placeholder={`Item ${idx + 1} `}
                                    />
                                    <SoftButton
                                        variant="text"
                                        color="error"
                                        iconOnly
                                        disabled={items.length <= 1}
                                        onClick={() => {
                                            const next = items.filter((_, i) => i !== idx);
                                            setItems(next.length ? next : [""]);
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 18 }}>close</Icon>
                                    </SoftButton>
                                </SoftBox>
                            ))}

                            <SoftBox>
                                <SoftButton
                                    variant="outlined"
                                    color="info"
                                    size="small"
                                    onClick={() => setItems([...items, ""])}
                                >
                                    <Icon sx={{ fontSize: 18 }}>add</Icon>
                                    &nbsp;Agregar item
                                </SoftButton>
                            </SoftBox>
                        </SoftBox>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SoftTypography variant="caption" color="text" fontWeight="bold">
                            Periodicidad
                        </SoftTypography>
                        <Select
                            fullWidth
                            value={periodicidad}
                            size="small"
                            disabled={isEdit}
                            onChange={(e) => {
                                const value = e.target.value;
                                setPeriodicidad(value);
                                if (value === "UNICA") setFechaLimite(null);
                            }}
                        >
                            <MenuItem value="UNICA">Única vez</MenuItem>
                            <MenuItem value="DIARIA">Diaria</MenuItem>
                            <MenuItem value="PERIODICA">Semanal</MenuItem>
                        </Select>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <InputLabel htmlFor="fechaLimite">
                            <SoftTypography component="label" variant="caption" fontWeight="bold">
                                Fecha límite
                            </SoftTypography>
                        </InputLabel>
                        <SoftInput
                            fullWidth
                            type="date"
                            size="small"
                            disabled={isEdit || !(periodicidad === "DIARIA" || periodicidad === "PERIODICA")}
                            value={fechaLimite ? fechaLimite.format("YYYY-MM-DD") : ""}
                            onChange={(e) => setFechaLimite(dayjs(e.target.value))}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                <SoftBox mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <SoftButton variant="outlined" color="dark" onClick={onClose}>
                        Cancelar
                    </SoftButton>
                    <SoftButton color="primary" onClick={handleSubmit} disabled={!canSubmit || loading}>
                        {loading ? "Guardando..." : "Guardar"}
                    </SoftButton>
                </SoftBox>

                <Modal
                    aria-labelledby="transition-modal-title"
                    aria-describedby="transition-modal-description"
                    open={openModalAddPlantiilla}
                    onClose={() => setOpenModalAddPlantiilla(false)}
                    closeAfterTransition
                >
                    <Fade in={openModalAddPlantiilla}>
                        <Card sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", maxHeight: "90%", width: { xs: "90%", sm: "40%", xxl: "30%" }, overflow: "auto" }}>
                            <SoftBox>
                                <SoftBox mb={1}>
                                    <SoftBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="start"
                                        p={3}
                                        pb={0}
                                    >
                                        <SoftBox>
                                            <SoftTypography variant="h6">Crear nueva plantilla</SoftTypography>
                                        </SoftBox>
                                        <SoftBox>
                                            <Tooltip title="Cerrar" placement="top">
                                                <Icon fontSize="medium" onClick={() => setOpenModalAddPlantiilla(false)} sx={{ cursor: "pointer" }}>
                                                    close
                                                </Icon>
                                            </Tooltip>
                                        </SoftBox>
                                    </SoftBox>
                                    <SoftBox p={3}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <InputLabel htmlFor="nombre">
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        Nombre de la plantilla
                                                    </SoftTypography>
                                                </InputLabel>
                                                <SoftInput
                                                    label="nombre"
                                                    placeholder="Ingrese un nombre"
                                                    type="text"
                                                    name="nombre"
                                                    value={data?.nombre || ""}
                                                    onChange={(e) => {
                                                        setData({
                                                            ...data,
                                                            [e.target.name]: e.target.value,
                                                        });
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </SoftBox>
                                </SoftBox>
                                <SoftBox display="flex" justifyContent="end" alignItems="center" p={3} pt={0}>
                                    <SoftBox mr={2}>
                                        <SoftButton color="primary" circular onClick={() => setOpenModalAddPlantiilla(false)}>
                                            <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                                            &nbsp;Cancelar
                                        </SoftButton>
                                    </SoftBox>
                                    <SoftBox>
                                        <SoftButton
                                            color="uproGreen"
                                            circular
                                            disabled={!data?.nombre || loadingPlantilla}
                                            onClick={handleSave}
                                        >
                                            {loadingPlantilla && <CircularProgress size={15} color="inherit" />}
                                            {!loadingPlantilla && <Icon sx={{ fontWeight: "light" }}>save</Icon>}
                                            &nbsp;Guardar
                                        </SoftButton>
                                    </SoftBox>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </Fade>
                </Modal>

            </Card>
        </Modal>
    );
}

ModalTarea.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedDate: PropTypes.any,
    initialTask: PropTypes.object,
    onSaved: PropTypes.func,
};
